import { Button } from '@/components/common/Button';
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/common/Dialog';
import { Input } from '@/components/common/Input';
import { Select } from '@/components/common/Select';
import { Textarea } from '@/components/common/Textarea';
import type { Order } from '@/domain/Order';
import { Card } from '../../DashboardCard';
import type { SupplementFormData } from './SupplementFactsForm';

const INSTRUCTION_OPTIONS = [
  {
    label: 'Take one packet after a meal in the first half of the day.',
    value: 'Take one packet after a meal in the first half of the day.',
  },
  {
    label: 'Take one packet after a meal in the second half of the day.',
    value: 'Take one packet after a meal in the second half of the day.',
  },
  {
    label: 'Take one pill after a meal in the first half of the day.',
    value: 'Take one pill after a meal in the first half of the day.',
  },
  {
    label: 'Take one pill after a meal in the second half of the day.',
    value: 'Take one pill after a meal in the second half of the day.',
  },
];

const CAPSULE_TYPE_OPTIONS = [
  { label: 'capsule', value: 'capsule' },
  { label: 'capsules', value: 'capsules' },
  { label: 'tablet', value: 'tablet' },
  { label: 'tablets', value: 'tablets' },
  { label: 'sachet', value: 'sachet' },
];

interface SupplementFactsFormOldProps {
  order: Order;
  formData: SupplementFormData;
  onFormDataChange: (data: Partial<SupplementFormData>) => void;
  onView: () => void;
  onExit: () => void;
}

/** Legacy supplement facts form (pre Lifetelos A4 redesign). */
export function SupplementFactsFormOld({
  order,
  formData,
  onFormDataChange,
  onView,
  onExit,
}: SupplementFactsFormOldProps) {
  const handleSupplementToggle = (index: number) => {
    const isChecked = formData.selectedSupplements.includes(index);
    const newSelected = isChecked
      ? formData.selectedSupplements.filter((i) => i !== index)
      : [...formData.selectedSupplements, index];
    onFormDataChange({
      selectedSupplements: newSelected,
      ...(!isChecked &&
        !(index in formData.quantities) && {
          quantities: { ...formData.quantities, [index]: 1 },
        }),
    });
  };

  const handleInstructionChange = (value: string) => {
    onFormDataChange({
      instruction: value,
      textarea: value,
    });
  };

  const handleTextareaChange = (value: string) => {
    onFormDataChange({ textarea: value });
    if (formData.instruction) {
      onFormDataChange({ instruction: '' });
    }
  };

  return (
    <Card className="max-h-[70vh] overflow-y-auto p-6">
      <DialogHeader>
        <DialogTitle className="text-3xl font-bold">
          Supplement Facts (Old)
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-4">
        <Input
          type="text"
          value={formData.name}
          onChange={(e) => onFormDataChange({ name: e.target.value })}
          label="Name"
        />
        {formData.selectedSupplements.length > 1 && (
          <p className="text-form-foreground text-lg">
            30 packets | {formData.selectedSupplements.length} supplements
          </p>
        )}

        {order.items.map((item, index) => (
          <div key={index} className="space-y-2">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={formData.selectedSupplements.includes(index)}
                onChange={() => handleSupplementToggle(index)}
                className="h-4 w-4 rounded border-gray-600 bg-slate-800"
              />
              <span className="font-medium">
                {item.name} | {item.strength}
              </span>
            </label>
            {formData.selectedSupplements.includes(index) && (
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={formData.quantities[index] ?? 1}
                  onChange={(e) =>
                    onFormDataChange({
                      quantities: {
                        ...formData.quantities,
                        [index]: parseInt(e.target.value) || 1,
                      },
                    })
                  }
                  min={1}
                  className="h-10"
                />
                <Select
                  options={CAPSULE_TYPE_OPTIONS}
                  value={formData.types[index] || 'capsule'}
                  onChange={(value) =>
                    onFormDataChange({
                      types: {
                        ...formData.types,
                        [index]: value,
                      },
                    })
                  }
                  className="h-10"
                />
              </div>
            )}
          </div>
        ))}
        <Select
          options={INSTRUCTION_OPTIONS}
          value={formData.instruction}
          onChange={handleInstructionChange}
          placeholder="Select instruction..."
        />

        <Textarea
          value={formData.textarea}
          onChange={(e) => handleTextareaChange(e.target.value)}
          label="Additional notes"
          rows={4}
          className="text-form-foreground bg-white"
        />

        <p className="text-form-foreground text-sm italic">
          Prepared and issued according to the patient&apos;s order. Follows the
          recommendations and advice of a doctor or pharmacist. Complete with
          food supplements and active ingredients available on the market.
        </p>
      </div>

      <DialogFooter className="mt-4 justify-start">
        <Button
          variant="primary"
          size={'md'}
          className="rounded text-base"
          onClick={onView}
        >
          View
        </Button>
        <Button
          variant="secondary"
          size={'md'}
          className="rounded text-base"
          onClick={onExit}
        >
          Exit
        </Button>
      </DialogFooter>
    </Card>
  );
}
